import React, { useState } from 'react';
import { Autocomplete, ErrorMessage, FormLabel, TextField } from '@biq/apex-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { Box, Grid } from '@mui/material';
import CodeMirror from '@uiw/react-codemirror';
import { useNavigate } from 'react-router-dom';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { JOB_MANAGER_PATHS } from '../../../../constants/job-manager-paths';
import { useAuth, usePermission } from '../../../../core';
import { useCreateDownloadJob } from '../../../../hooks/mutateCreateDownloadJob';
import { CodeMirrorStyles, validationsMessages } from '../../../../lib';
import { downloadFileTypes } from '../../job-manager-helper';
import { CreateJobDialog } from '../create-job-dialog';
import {
    createDownloadJobDefaults,
    CreateDownloadJobFormInputs,
    createDownloadJobSchema,
    createDownloadJobStyles,
} from './create-download-job-helper';
import JobExecutor from '../job-executor/JobExecutor';
import { JobFieldHeadingWithTooltip } from '../job-executor/JobFieldHeadingWithTooltip';

export const CreateDownloadJob = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const navigate = useNavigate();
    const { hasPermissions } = usePermission();

    const { mutate: createDownloadJob } = useCreateDownloadJob();

    const [downloadFileType, setDownloadFileType] = useState<string>('');
    const [query, setQuery] = useState<string>('');
    const [password, setPassword] = useState<string | undefined>(undefined);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const {
        handleSubmit,
        formState: { errors },
        control,
        clearErrors,
        setValue,
    } = useForm<CreateDownloadJobFormInputs>({
        mode: 'all',
        resolver: yupResolver(createDownloadJobSchema),
        defaultValues: createDownloadJobDefaults,
    });

    const onQueryChange = React.useCallback((value: string) => {
        setQuery(value);
        setValue('query', value);
        clearErrors('query');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePasswordChange = (value: string): void => {
        setPassword(value);
        setValue('password', value);
    };

    const handleStartUpDownloadJob = (): void => {
        // First try to validate the query. If the query validation is successful then try to create the download job.
        // Otherwise, display an error coming from the server.
        setIsLoading(true);
        createDownloadJob(
            {
                fileType: downloadFileType,
                query,
                password,
                token: getToken(),
            },
            {
                onSuccess: () => {
                    setTimeout(() => {
                        navigate(JOB_MANAGER_PATHS.LANDING_PAGE);
                    }, 500);
                    setIsLoading(false);
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const onSubmit = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            handleSubmit(handleStartUpDownloadJob)();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    return (
        <CreateJobDialog
            handleSubmit={onSubmit}
            buttonText='START JOB'
            isLoading={isLoading}
        >
            <Grid sx={createDownloadJobStyles.container}>
                <Grid sx={createDownloadJobStyles.bodyContainer}>
                    <Grid sx={createDownloadJobStyles.containerWidth}>
                        <JobExecutor />
                    </Grid>
                    <Grid sx={createDownloadJobStyles.containerWidth}>
                        <JobFieldHeadingWithTooltip
                            label='File Type'
                            title={
                                'For hierarchical or structured data, use JSON. CSV and XLSX are best for flat/tabular data.'
                            }
                            required
                            popperStyles={createDownloadJobStyles.popperStyles}
                            modifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 4],
                                    },
                                },
                            ]}
                        />
                        <Controller
                            name='fileType'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <Autocomplete
                                    {...field}
                                    options={downloadFileTypes}
                                    onChange={(__, value): void => {
                                        field.onChange(value);
                                        setDownloadFileType(value);
                                    }}
                                    sx={
                                        createDownloadJobStyles.autoCompleteStyles
                                    }
                                    renderInput={(
                                        params
                                    ): React.JSX.Element => (
                                        <TextField
                                            {...params}
                                            size='medium'
                                            required={true}
                                            variant='outlined'
                                            error={!!errors.fileType}
                                            errorText={
                                                errors.fileType
                                                    ? validationsMessages.required
                                                    : ''
                                            }
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid sx={createDownloadJobStyles.containerWidth}>
                        <Controller
                            name='query'
                            control={control}
                            render={(): React.JSX.Element => (
                                <>
                                    <FormLabel
                                        required
                                        label='Query'
                                        sx={createDownloadJobStyles.queryLabel}
                                    />
                                    <CodeMirror
                                        value={query}
                                        style={{
                                            ...(errors?.query
                                                ? createDownloadJobStyles.queryError
                                                : createDownloadJobStyles.query),
                                        }}
                                        height={
                                            createDownloadJobStyles.queryErrorHeight
                                        }
                                        extensions={[CodeMirrorStyles]}
                                        onChange={onQueryChange}
                                    />
                                    {errors?.query && (
                                        <Box
                                            style={
                                                createDownloadJobStyles.errorContainer
                                            }
                                        >
                                            <ErrorMessage
                                                errorMessage={
                                                    validationsMessages.required
                                                }
                                            />
                                        </Box>
                                    )}
                                </>
                            )}
                        />
                    </Grid>
                    <Grid sx={createDownloadJobStyles.containerWidth}>
                        <Grid sx={createDownloadJobStyles.passwordContainer}>
                            <Controller
                                name='password'
                                control={control}
                                render={({ field }): React.JSX.Element => (
                                    <TextField
                                        {...field}
                                        variant='outlined'
                                        required={false}
                                        label='Password'
                                        visibilityIcon
                                        value={field.value}
                                        error={!!errors.password}
                                        errorText={
                                            errors.password
                                                ? errors.password.message
                                                : ''
                                        }
                                        onChange={(e): void =>
                                            handlePasswordChange(e.target.value)
                                        }
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateJobDialog>
    );
};
